import React, {useState, useEffect, useContext} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {UserContext} from '../../UserContext';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';

import styles from './DetalharConsumoCliente.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import {formatarKwh10_2} from "../../util/validacoes";

export default function DetalharConsumoCliente() {
    const {user} = useContext(UserContext);
    const navigate = useNavigate();
    const {get} = useHttpClient();
    const {exibirErro} = useNotificacoes();

    const {id} = useParams();
    const [usinas, setUsinas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [chaveAsaas, setChaveAsaas] = useState(false);

    const [formData, setFormData] = useState({
        idUsina: '',
        idCliente: '',
        bandeira: '',
        dtVencimento: '',
        saldoAtualKwh: '',
        mesConsumo: '',
        dtInicioConsumo: '',
        dtFimConsumo: '',
        qtdConsumoKw: '',
        mesInjecao: '',
        dtInicioInjecao: '',
        dtFimInjecao: '',
        qtdInjetadaKw: '',
        vlTarifa: '',
        qtdCompensadoKw: '',
        vlTaxaIluminacao: '',
        vlJurosMultaCredito: '',
        vlTusd: '',
        codigoBarras: '',
        linhaDigitavel: '',
        unidadeNome: ''
    });

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    useEffect(() => {
        if (!user?.empresa?.id) return;

        let cancelado = false;

        (async () => {
            try {
                const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                if (cancelado) return;

                const lista = Array.isArray(data) ? data : (data?.content ?? []);
                setUsinas(lista);
            } catch (err) {
                if (cancelado) return;
                setUsinas([]);
                exibirErro('Erro ao buscar usinas: ' + (err.message || ''));
            }
        })();

        return () => {
            cancelado = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.empresa?.id]);

    useEffect(() => {
        if (!id) return;

        let cancelado = false;

        (async () => {
            try {
                const data = await get(`/hello-sol/consumos-cliente/${id}`);
                if (cancelado) return;

                const idUsinaSel = data?.usina?.id ?? '';
                const idClienteSel = data?.unidadeConsumidora?.cliente?.id ?? '';

                setFormData({
                    idUsina: idUsinaSel,
                    idCliente: idClienteSel,
                    unidadeNome: data?.unidadeConsumidora?.nomeUnidade ?? '',
                    bandeira: data?.bandeira ?? '',
                    dtVencimento: data?.dtVencimento
                        ? new Date(data.dtVencimento).toLocaleDateString('pt-BR', {timeZone: 'UTC'})
                        : '',
                    mesConsumo: String(data?.mesConsumo ?? ''),
                    dtInicioConsumo: data?.dtInicioConsumo
                        ? new Date(data.dtInicioConsumo).toLocaleDateString('pt-BR', {timeZone: 'UTC'})
                        : '',
                    dtFimConsumo: data?.dtFimConsumo
                        ? new Date(data.dtFimConsumo).toLocaleDateString('pt-BR', {timeZone: 'UTC'})
                        : '',
                    qtdConsumoKw: formatarKwh10_2(String(data?.qtdConsumoKw ?? '')),
                    saldoAtualKwh: formatarKwh10_2(String(data?.saldoAtualKwh ?? '')),
                    mesInjecao: String(data?.mesInjecao ?? ''),
                    dtInicioInjecao: data?.dtInicioInjecao
                        ? new Date(data.dtInicioInjecao).toLocaleDateString('pt-BR', {timeZone: 'UTC'})
                        : '',
                    dtFimInjecao: data?.dtFimInjecao
                        ? new Date(data.dtFimInjecao).toLocaleDateString('pt-BR', {timeZone: 'UTC'})
                        : '',
                    qtdInjetadaKw: formatarKwh10_2(String(data?.qtdInjetadaKw ?? '')),
                    vlTarifa: formatValorTarifa(String(data?.vlTarifa ?? '')),
                    qtdCompensadoKw: formatarKwh10_2(String(data?.qtdCompensadoKw ?? '')),
                    vlTaxaIluminacao: formatValorMonetario(String(data?.vlTaxaIluminacao ?? '')),
                    vlJurosMultaCredito: formatValorMonetario(String(data?.vlJurosMultaCredito ?? '')),
                    vlTusd: formatValorMonetario(String(data?.vlTusd ?? '')),
                    codigoBarras: data?.codigoBarras ?? '',
                    linhaDigitavel: data?.linhaDigitavel ?? ''
                });

                try {
                    if (idUsinaSel) {
                        const usuariosData = await get(
                            `/hello-sol/usuarios/buscarUsuarioUsina?idUsina=${idUsinaSel}&idTipoUsuario=3`
                        );
                        if (cancelado) return;

                        const listaUsuarios = Array.isArray(usuariosData)
                            ? usuariosData
                            : (usuariosData?.content ?? []);
                        setUsuarios(listaUsuarios);

                        const chave = await get(`/hello-sol/usinas/${idUsinaSel}/chave-asaas`);
                        if (cancelado) return;

                        setChaveAsaas(!!chave);
                    } else {
                        setUsuarios([]);
                        setChaveAsaas(false);
                    }
                } catch (err) {
                    if (cancelado) return;
                    setUsuarios([]);
                    setChaveAsaas(false);
                }
            } catch (err) {
                if (cancelado) return;
                exibirErro('Erro ao carregar consumo: ' + (err.message || ''));
            }
        })();

        return () => {
            cancelado = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatPotencia = (value) => {
        const numericValue = parseFloat(String(value).replace(',', '.').replace(/[^\d.]/g, ''));
        return isNaN(numericValue)
            ? "0,00"
            : Math.min(numericValue, 9999999999.99).toFixed(2).replace('.', ',');
    };

    const handleDateInput = (e, fieldName) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 2) value = value.replace(/^(\d{2})(\d)/, "$1/$2");
        if (value.length > 5) value = value.replace(/^(\d{2}\/\d{2})(\d+)/, "$1/$2");
        setFormData(prev => ({...prev, [fieldName]: value}));
    };

    const formatValorMonetario = (value) => {
        const numericValue = parseFloat(
            String(value)
                .replace(/\./g, '')
                .replace(',', '.')
                .replace(/[^\d.]/g, '')
        );

        return isNaN(numericValue)
            ? '0,00'
            : numericValue.toFixed(2).replace('.', ',');
    };

    const formatValorTarifa = (value) => {
        const numericValue = parseFloat(
            String(value)
                .replace(/\./g, '')
                .replace(',', '.')
                .replace(/[^\d.]/g, '')
        );

        return isNaN(numericValue)
            ? '0,0000000'
            : numericValue.toFixed(7).replace('.', ',');
    };

    return (
        <div className={styles_global.container}>
            <h2>Detalhar Consumo</h2>

            <form>
                <div className={styles.linhaConsumoCliente3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina</label>
                        <select
                            name="idUsina"
                            value={formData.idUsina}
                            className={styles.campoUsina}
                            disabled
                        >
                            <option value="">Selecionar Usina</option>
                            {(usinas || []).map(usina => (
                                <option key={usina.id} value={usina.id}>{usina.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idCliente">Cliente</label>
                        <select
                            name="idCliente"
                            value={formData.idCliente}
                            className={styles.campoCliente}
                            disabled
                        >
                            <option value="">Selecionar Cliente</option>
                            {(usuarios || []).map(usuario => (
                                <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label>Unidade</label>
                        <input
                            type="text"
                            value={formData.unidadeNome}
                            className={styles.campoUnidade}
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="bandeira">Bandeira</label>
                        <select
                            name="bandeira"
                            value={formData.bandeira}
                            disabled
                        >
                            <option value="">Selecionar Bandeira</option>
                            <option value="VERDE">Verde</option>
                            <option value="AMARELA">Amarela</option>
                            <option value="VERMELHA">Vermelha</option>
                            <option value="ESCASSEZ HIDRICA">Escassez Hídrica</option>
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="dtVencimento">Data de Vencimento da Fatura do Cliente</label>
                        <input
                            type="text"
                            name="dtVencimento"
                            className={styles.campoDtVencimento}
                            value={formData.dtVencimento || ''}
                            onChange={(e) => handleDateInput(e, 'dtVencimento')}
                            placeholder="dd/mm/aaaa"
                            maxLength={10}
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="saldoAtualKwh">Saldo Atual KWh</label>
                        <input
                            type="text"
                            name="saldoAtualKwh"
                            className={styles.camposaldoAtualKwh}
                            value={formData.saldoAtualKwh || ''}
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="mesConsumo">Período de Consumo</label>
                        <select
                            name="mesConsumo"
                            className={styles.campoPeriodoConsumo}
                            value={formData.mesConsumo}
                            disabled
                        >
                            <option value="">Selecionar Mês</option>
                            {[...Array(12)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString('pt-BR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="qtdConsumoKw">Quantidade KWh Consumido</label>
                        <input
                            name="qtdConsumoKw"
                            className={styles.campoQtdConsumo}
                            value={formData.qtdConsumoKw}
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="mesInjecao">Período de Injeção</label>
                        <select
                            name="mesInjecao"
                            className={styles.campoPeriodoInjecao}
                            value={formData.mesInjecao}
                            disabled
                        >
                            <option value="">Selecionar Mês</option>
                            {[...Array(12)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString('pt-BR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="qtdInjetadaKw">Quantidade KWh Injetado</label>
                        <input
                            name="qtdInjetadaKw"
                            className={styles.campoQtdInjetado}
                            value={formData.qtdInjetadaKw}
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="vlTarifa">Valor da Tarifa R$/KWh (com imposto)</label>
                        <input
                            name="vlTarifa"
                            className={styles.campoVlTarifa}
                            value={formData.vlTarifa}
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="qtdCompensadoKw">Quantidade KWh Compensado</label>
                        <input
                            name="qtdCompensadoKw"
                            className={styles.campoQtdCompensado}
                            value={formData.qtdCompensadoKw}
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="vlTaxaIluminacao">Valor da Taxa de Iluminação</label>
                        <input
                            name="vlTaxaIluminacao"
                            className={styles.campoVlTarifa}
                            value={formData.vlTaxaIluminacao}
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="vlJurosMultaCredito">Valor de Juros e Multa</label>
                        <input
                            name="vlJurosMultaCredito"
                            className={styles.campoVlJuros}
                            value={formData.vlJurosMultaCredito}
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="vlTusd">Desconto TUSD</label>
                        <input
                            name="vlTusd"
                            className={styles.campoVlTarifa}
                            value={formData.vlTusd || ''}
                            disabled
                        />
                    </div>

                    {chaveAsaas && formData.codigoBarras && (
                        <div className={styles_content.formField}>
                            <label htmlFor="codigoBarras">Código de Barras:</label>
                            <input
                                type="text"
                                id="codigoBarras"
                                name="codigoBarras"
                                value={formData.codigoBarras}
                                disabled
                            />
                        </div>
                    )}

                    {chaveAsaas && formData.linhaDigitavel && (
                        <div className={styles_content.formField}>
                            <label htmlFor="linhaDigitavel">Linha Digitável:</label>
                            <input
                                type="text"
                                id="linhaDigitavel"
                                name="linhaDigitavel"
                                value={formData.linhaDigitavel}
                                disabled
                            />
                        </div>
                    )}
                </div>

                <div className={styles_content.buttonGroup}>
                    <button
                        type="button"
                        onClick={() => navigate('/home/listar-cliente-consumo')}
                    >
                        Voltar
                    </button>
                </div>
            </form>
        </div>
    );
}